// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type {
  ConsumptionRecord,
  DayLedgerResponse,
  MealInstance,
  ReplacementFood,
} from "@bitebalance/contracts"
import type { I18nContextValue } from "@/i18n"
import { formatKcal } from "@/i18n"
import type { Locale } from "@/i18n"

/**
 * ADR-056 (2026-09-05): what the user recorded as eaten is IMMUTABLE user
 * history keyed by (user, meal DATE in the product's calendar, SLOT). Plan
 * revisions are mutable recommendations that RE-ISSUE wire meal ids —
 * after a rebuild `breakfast_d0` names TODAY's breakfast where it named
 * yesterday's before — so an outcome mark is NEVER looked up by wire id.
 *
 * This module is the ONE builder both pages use (2026-09-03 consistency
 * finding: Today and Plan once disagreed on the same meal). Today builds
 * marks from the day ledger's canonical `consumedMeals`; Plan from the
 * history endpoint's days plus the same ledger — the same records, so the
 * pages cannot disagree after a reload, and yesterday's marks survive
 * every apply, undo and rebuild.
 */

/** What a card shows for a linked replacement — combined across every entry that replaced the slot. */
export interface ReplacementDisplay {
  /** Descriptions joined ("Döner kebab, Pommes") when more than one entry replaced the same slot. */
  description: string
  /** Summed kcal across every contributing entry. */
  kcal: number
  /** true when any contributing entry is below "high" confidence — the card prefixes its kcal with "≈". */
  estimated: boolean
}

export interface MealOutcomeExtras {
  /** Persisted outcome key ("had" | "partial" | "skipped" | "changed"). */
  outcome: ConsumptionRecord["outcome"]
  /** partial only: the calories COUNTED at logging vs the card's planned portion. */
  detail: string | null
  /**
   * ADR-078: changed only, and only when a server-linked off-plan entry
   * replaced THIS slot specifically — never a day-wide guess. A legacy or
   * manually-logged "changed" row (no link) is null here; the card then
   * shows only the plain outcome label, nothing invented retroactively.
   */
  replacement: ReplacementDisplay | null
}

/**
 * The facts a mark is built from: a ConsumptionRecord, or — for a ledger
 * served before `consumedMeals` existed — the same facts read off the
 * ledger's own rows (still keyed by the ledger's DATE and the row's slot).
 */
export interface OutcomeFacts {
  date: string
  slot: string
  outcome: ConsumptionRecord["outcome"]
  /** Calories counted when the outcome was logged; null = honestly unknown
   * (an `unavailable` snapshot) — never estimated from the current plan. */
  consumedKcal: number | null
  /** ADR-078: off-plan food that replaced THIS (date, slot) — server-joined, present only when linked. */
  replacedBy?: readonly ReplacementFood[]
}

function combineReplacements(items: readonly ReplacementFood[]): ReplacementDisplay {
  return {
    description: items.map(i => i.description).join(", "),
    kcal: items.reduce((sum, i) => sum + i.kcal, 0),
    estimated: items.some(i => i.confidence !== "high"),
  }
}

/** The identity of a consumption record on every client: (meal date, slot). */
export function consumptionKey(date: string, slot: string): string {
  return `${date}|${slot}`
}

export function outcomeFactsFromRecord(record: ConsumptionRecord): OutcomeFacts {
  return {
    date: record.date,
    slot: record.slot,
    outcome: record.outcome,
    consumedKcal: record.snapshotStatus === "unavailable" ? null : record.consumedKcal,
    replacedBy: record.replacedBy,
  }
}

/**
 * Today's ledger as outcome facts: its canonical `consumedMeals` records.
 * A ledger stored before the field existed (idempotency replays) carries
 * the same facts on its planned rows — read by the ledger's date and the
 * row's slot, never by the row's wire meal id; `consumedKcalStatus:
 * "unavailable"` is an unknown amount, not 0.
 */
export function outcomeFactsFromTodayLedger(ledger: DayLedgerResponse | null): OutcomeFacts[] {
  if (!ledger) return []
  if (ledger.consumedMeals) return ledger.consumedMeals.map(outcomeFactsFromRecord)
  const facts: OutcomeFacts[] = []
  for (const row of ledger.plannedMeals) {
    if (!row.outcome) continue
    facts.push({
      date: ledger.date,
      slot: row.slot,
      outcome: row.outcome,
      consumedKcal: row.consumedKcalStatus === "unavailable" ? null : row.consumedKcal,
      replacedBy: row.replacedBy,
    })
  }
  return facts
}

export type OutcomeFactsIndex = ReadonlyMap<string, OutcomeFacts>

/** Index facts by (date, slot). Later sources win — pass the freshest last. */
export function indexOutcomeFacts(...sources: Array<Iterable<OutcomeFacts>>): OutcomeFactsIndex {
  const index = new Map<string, OutcomeFacts>()
  for (const source of sources) {
    for (const facts of source) index.set(consumptionKey(facts.date, facts.slot), facts)
  }
  return index
}

/**
 * THE builder. `plannedKcal` is the card's planned portion; the consumed
 * figure is the record's, rounded once at logging and never recomputed
 * from today's (possibly revised) plan. `facts.replacedBy` is already
 * scoped to THIS slot by the server (services/planning/src/history/
 * replacements.ts) — never a day-wide list to filter or guess from here.
 */
export function buildOutcomeExtras(
  facts: OutcomeFacts,
  plannedKcal: number,
  t: I18nContextValue["t"],
  locale: Locale
): MealOutcomeExtras {
  let detail: string | null = null
  if (facts.outcome === "partial" && facts.consumedKcal !== null) {
    detail = t("views.mealCard.partialDetail", {
      consumed: formatKcal(facts.consumedKcal, locale),
      planned: formatKcal(plannedKcal, locale),
    })
  }

  const replacement =
    facts.outcome === "changed" && facts.replacedBy && facts.replacedBy.length > 0
      ? combineReplacements(facts.replacedBy)
      : null

  return { outcome: facts.outcome, detail, replacement }
}

export function outcomeExtrasFromRecord(
  record: ConsumptionRecord,
  plannedKcal: number,
  t: I18nContextValue["t"],
  locale: Locale
): MealOutcomeExtras {
  return buildOutcomeExtras(outcomeFactsFromRecord(record), plannedKcal, t, locale)
}

/**
 * The mark for one planned meal card: the canonical facts for the card's
 * (date, slot), or null when nothing was recorded for that meal.
 */
export function mealOutcomeExtras(
  index: OutcomeFactsIndex,
  meal: Pick<MealInstance, "date" | "slot" | "nutrition">,
  t: I18nContextValue["t"],
  locale: Locale
): MealOutcomeExtras | null {
  const facts = index.get(consumptionKey(meal.date, meal.slot))
  if (!facts) return null
  return buildOutcomeExtras(facts, meal.nutrition.kcal, t, locale)
}
